import { type AppState, Auth0Provider } from '@auth0/auth0-react'
import { type ReactNode } from 'react'

import { navigateSuite } from '~/shell/app/router'
import { getAuthConfig } from '~/shell/config'

import { resolveReturnTo } from './resolveReturnTo'

interface Auth0ProviderWithConfigProps {
  children: ReactNode
}

/**
 * Returns the user to where they were before Universal Login.
 *
 * `redirect_uri` is the bare origin (Auth0 allows only a fixed callback URL), so
 * the pre-login location travels in `appState.returnTo` instead. Auth0 sends the
 * user back to `/`, which would otherwise fall through `Bootstrap` to the default
 * section; navigating the data router to the resolved `returnTo` here (before
 * auth0-react flips `isAuthenticated` and the guarded tree renders) lands them
 * on their original URL. The default callback's `history.replaceState` is invisible
 * to a `createBrowserRouter` data router, so it must be `navigateSuite`.
 */
const onRedirectCallback = (appState?: AppState) => {
  navigateSuite(resolveReturnTo(appState))
}

/**
 * Wraps its children in an `Auth0Provider` configured from runtime config.
 *
 * Reads the Auth0 domain, client ID, and audience via `getAuthConfig()`, which
 * throws `RegardConfigError` when the domain or client ID is missing — making a
 * misconfigured boot fail fast before the React tree renders. `onRedirectCallback`
 * returns the user to their pre-login location after a successful login.
 */
export const Auth0ProviderWithConfig = ({
  children,
}: Auth0ProviderWithConfigProps) => {
  const { audience, clientId, domain } = getAuthConfig()

  return (
    <Auth0Provider
      authorizationParams={{
        audience,
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
      clientId={clientId}
      domain={domain}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
    >
      {children}
    </Auth0Provider>
  )
}
